import html2pdf from 'html2pdf.js'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../supabase/client' // Ensure Supabase client is imported
import './ResultsPage.css'
import TalentIcon from './TalentIcon'
import TalentLevels from './TalentLevels'

interface PersonalInfo {
  firstName?: string
  lastName?: string
  position?: string
  company?: string
}

// Вынесем секцию таланта в отдельный компонент для лучшей организации (точная копия из ResultsPage)
const TalentSection = ({
  talent,
  talentData,
  expandedTalents,
  toggleTalentIPR,
  formatSkills,
  getLevels,
}) => {
  const levels = getLevels(talent.Levels)
  const currentLevelDescription =
    talentData.level > 0 && talentData.level <= levels.length
      ? levels[talentData.level - 1]
      : 'Уровень не определен'

  const softSkills = formatSkills(talent.Soft)
  const hardSkills = formatSkills(talent.Hard)

  return (
    <div className="talent-section">
      {/* Группа элементов, которые должны быть на одной странице */}
      <div className="talent-info-group">
        <h3 className="talent-title">{talent.Tittle}</h3>
        <div className="business-roles">
          <TalentIcon talentId={talent.Number} name={talent.Tittle} />
          {talent.Role}
        </div>
      </div>

      {/* Основное описание таланта */}
      <div className="talent-info-group">
        <div className="talent-description">
          <strong>Потенциал:</strong>
          <p>{talent.Potential}</p>
        </div>
      </div>

      {/* Ответ на вопрос */}
      <div className="talent-info-group">
        <div className="talent-description">
          <strong>Опыт применения таланта:</strong>
          <p className="response-text">
            <em>Вопрос: {talent.Questions}</em>
            <br />
            <strong>Ответ:</strong> {talentData.response || 'Нет ответа'}
          </p>
        </div>
      </div>

      {/* Текущий уровень */}
      <div className="talent-info-group">
        <div className="talent-description">
          <strong>Текущий уровень развития:</strong>
          <p>{currentLevelDescription}</p>
        </div>
      </div>

      {/* Числовые индикаторы */}
      <TalentLevels level={talentData.level} satisfaction={talentData.satisfaction} />

      {/* Кнопка ИПР - не отображается в PDF */}
      <div className="ipr-button-container no-print">
        <button className="ipr-button" onClick={() => toggleTalentIPR(talent.Number)}>
          {expandedTalents[talent.Number] ? 'Скрыть Софт/Хард навыки' : 'Показать Софт/Хард навыки'}
        </button>
      </div>

      {/* План развития */}
      {expandedTalents[talent.Number] && (
        <div className="ipr-section">
          <h4>Навыки и компетенции</h4>

          {softSkills.length > 0 && (
            <div className="skills-section">
              <h5>Софт-скиллы для развития:</h5>
              <div className="skills-list">
                {softSkills.map((skill, idx) => (
                  <div key={`soft-${idx}`} className="skill-item">
                    <div className="skill-number">{idx + 1}</div>
                    <div className="skill-text">{skill}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hardSkills.length > 0 && (
            <div className="skills-section">
              <h5>Хард-скиллы и профессиональные компетенции:</h5>
              <div className="skills-list">
                {hardSkills.map((skill, idx) => (
                  <div key={`hard-${idx}`} className="skill-item">
                    <div className="skill-number">{idx + 1}</div>
                    <div className="skill-text">{skill}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Полностью идентичный ResultsPage компонент
const FullReportPage = () => {
  const { userId } = useParams()
  const navigate = useNavigate()

  // Состояния для данных
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedTalents, setExpandedTalents] = useState({})
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)

  // Данные профиля и ответов
  const [personalInfo, setPersonalInfo] = useState<PersonalInfo>({})
  const [talents, setTalents] = useState([])
  const [talentIndices, setTalentIndices] = useState([])
  const [talentResponses, setTalentResponses] = useState([])
  const [talentLevels, setTalentLevels] = useState([])
  const [satisfactionLevels, setSatisfactionLevels] = useState([])

  // Загрузка данных
  useEffect(() => {
    const fetchFullReportData = async () => {
      try {
        setLoading(true)

        console.log(`Запрос данных профиля для ${userId}`)

        const { data: profile, error: supabaseError } = await supabase
          .from('ipr_profiles')
          .select('*')
          .eq('userId', userId)
          .single()

        if (supabaseError) {
          throw new Error(supabaseError.message || 'Ошибка при загрузке профиля')
        }

        if (!profile) {
          throw new Error('Profile not found')
        }

        console.log('Данные профиля:', profile)

        const profileDataParsed = JSON.parse(profile.profileData || '{}') as any
        setPersonalInfo(profile as PersonalInfo)
        const profileData = profileDataParsed as { talents: any[] }
        const profileTalents = profileData.talents || []
        const talentIds = profileTalents.map((talent) => talent.id)
        setTalentIndices(talentIds)
        setTalents(profileTalents)

        // Проверяем, есть ли данные талантов
        if (talentIds.length === 0) {
          throw new Error('Профиль не содержит данных о талантах')
        }

        // Загружаем данные о талантах из базы
        const { data: allTalents, error: talentsError } = await supabase
          .from('ipr_business_talents')
          .select('*')

        if (talentsError) {
          console.warn('Не удалось загрузить данные о талантах из базы')
          // Продолжаем с доступными данными
        }

        console.log('Загружено талантов из базы:', allTalents.length, allTalents)
        setTalents(allTalents)

        // Формируем массив с ответами на вопросы из данных профиля
        // Напрямую извлекаем ответы из данных профиля
        const responses = []
        profileTalents.forEach((talent) => {
          if (talent.response) {
            responses.push({
              talentId: talent.id,
              response: talent.response,
            })
          }
        })
        setTalentResponses(responses)

        // Формируем массив с уровнями талантов
        const levels = profileTalents.map((talent) => ({
          talentId: talent.id,
          level: talent.level || 0,
        }))
        setTalentLevels(levels)

        // Формируем массив с уровнями удовлетворенности
        const satisfaction = profileTalents.map((talent) => ({
          talentId: talent.id,
          level: talent.satisfaction || 0,
        }))
        setSatisfactionLevels(satisfaction)
      } catch (err) {
        console.error('Ошибка при загрузке данных:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchFullReportData()
  }, [userId])

  // Переключение отображения ИПР для таланта
  const toggleTalentIPR = (talentId) => {
    setExpandedTalents((prev) => ({
      ...prev,
      [talentId]: !prev[talentId],
    }))
  }

  // Форматирование навыков
  const formatSkills = (skillsText) => {
    if (!skillsText) return []

    return skillsText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
  }

  // Обработка описания уровней
  const getLevels = (levelsString) => {
    if (!levelsString) return ['Нет данных']

    // Извлекаем все описания уровней
    const descriptions = [
      levelsString.includes('Нет (не развита):')
        ? levelsString
            .split('Нет (не развита):')[1]
            .split(/На низком уровне:|$/, 2)[0]
            .trim()
        : '',
      levelsString.includes('На низком уровне:')
        ? levelsString
            .split('На низком уровне:')[1]
            .split(/Средний уровень:|$/, 2)[0]
            .trim()
        : '',
      levelsString.includes('Средний уровень:')
        ? levelsString
            .split('Средний уровень:')[1]
            .split(/Выше среднего:|$/, 2)[0]
            .trim()
        : '',
      levelsString.includes('Выше среднего:')
        ? levelsString
            .split('Выше среднего:')[1]
            .split(/Высокий уровень:|$/, 2)[0]
            .trim()
        : '',
      levelsString.includes('Высокий уровень:')
        ? levelsString.split('Высокий уровень:')[1].trim()
        : '',
    ]

    return descriptions
  }

  // Helper function to get talent level and satisfaction
  const getTalentData = (talentId) => {
    const levelData = talentLevels.find((item) => Number(item.talentId) === Number(talentId))
    const satisfactionData = satisfactionLevels.find(
      (item) => Number(item.talentId) === Number(talentId),
    )
    const responseData = talentResponses.find((item) => Number(item.talentId) === Number(talentId))

    return {
      level: levelData ? levelData.level : 0,
      satisfaction: satisfactionData ? satisfactionData.level : 0,
      response: responseData ? responseData.response : '',
    }
  }

  // Функция для поиска таланта по ID
  const getTalentById = (talentId) => {
    console.log(`Ищем талант с ID ${talentId}. Всего талантов в базе: ${talents.length}`)

    // Преобразуем ID к числу для корректного сравнения
    const numericTalentId = Number(talentId)

    // Проверяем наличие таланта в базе талантов по номеру (учитываем разные варианты именования полей)
    const talentFromDB = talents.find((talent) => {
      const talentNumber = Number(talent.Number || talent.number)
      return talentNumber === numericTalentId
    })

    if (talentFromDB) {
      console.log(
        `Найден талант в базе: ${talentFromDB.Tittle || talentFromDB.tittle || talentFromDB.Title || talentFromDB.title}`,
      )
      return talentFromDB
    }

    // Если талант не найден, ищем по номеру в других форматах
    console.log('Выполняем расширенный поиск таланта в базе данных...')

    // Выведем все доступные таланты для отладки
    talents.forEach((t, idx) => {
      console.log(
        `Талант ${idx + 1}: ${t.Number || t.number} - ${t.Tittle || t.tittle || t.Title || t.title}`,
      )
    })

    // Ищем талант, проверяя все возможные варианты полей
    for (const talent of talents) {
      // Проверяем все возможные варианты названия поля с номером таланта
      const talentNum = talent.Number || talent.number
      if (Number(talentNum) === numericTalentId) {
        console.log(
          `Найден талант с ID ${talentId}: ${talent.Tittle || talent.tittle || talent.Title || talent.title}`,
        )
        return talent
      }
    }

    // Если талант не найден, создаем шаблон с максимально возможной информацией
    console.log(`Талант с ID ${talentId} не найден в базе данных, создаем шаблон.`)

    // Попробуем найти талант с подобным номером, чтобы узнать его название
    const similarTalent = talents.find((t) =>
      String(t.Number || t.number).includes(String(talentId)),
    )
    const talentTitle = similarTalent
      ? similarTalent.Tittle || similarTalent.tittle || similarTalent.Title || similarTalent.title
      : `Талант ${talentId}`

    console.log(`Создаем шаблон для таланта ${talentId} с названием: ${talentTitle}`)

    // Создаем базовую структуру таланта с максимально доступной информацией
    return {
      Number: talentId,
      Tittle: talentTitle,
      Role: 'Роль не определена', // Можно попробовать найти похожие роли из других талантов
      Potential: 'Информация о потенциале отсутствует',
      Questions: 'Вопрос отсутствует в базе данных',
      Levels: '',
      Soft: '',
      Hard: '',
    }
  }

  // Генерация PDF
  const generatePDF = async () => {
    try {
      setIsGeneratingPDF(true)

      // Создаем временный элемент для генерации PDF
      const printElement = document.createElement('div')
      printElement.innerHTML = document.getElementById('report-content').innerHTML
      printElement.classList.add('pdf-container')
      printElement.classList.add('pdf-version')

      document.body.appendChild(printElement)

      // Обеспечиваем, чтобы заголовки талантов не отрывались от содержимого
      const talentSections = printElement.querySelectorAll('.talent-section')
      talentSections.forEach((section) => {
        // Группируем заголовок и основную информацию
        const title = section.querySelector('.talent-title')
        const role = section.querySelector('.business-roles')
        const potentialDesc = section.querySelector('.talent-description')

        if (title && role && potentialDesc) {
          // Создаем обертку для критически важных элементов, которые нельзя разрывать
          const wrapper = document.createElement('div')
          wrapper.classList.add('talent-info-group')

          // Перемещаем элементы в обертку
          title.parentNode.insertBefore(wrapper, title)
          wrapper.appendChild(title)
          wrapper.appendChild(role)
          wrapper.appendChild(potentialDesc)
        }

        // Преобразуем бизнес-роли в специальный формат для PDF
        if (role) {
          const roleText = role.textContent
          const roleLabel = roleText.split(':')[0]
          const roleValue = roleText.split(':')[1] || ''

          // Создаем новый элемент для бизнес-ролей с улучшенным стилем
          const newRoleElement = document.createElement('div')
          newRoleElement.classList.add('business-roles')
          newRoleElement.innerHTML = roleValue

          // Заменяем старый элемент
          role.parentNode.replaceChild(newRoleElement, role)
        }
      })

      // Настройки для PDF с улучшенными параметрами разбиения на страницы
      const opt = {
        margin: [15, 15, 15, 15], // [мм] верх, право, низ, лево
        filename: `business_evolution_report_${personalInfo.lastName || 'user'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true,
          scrollY: 0,
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
          compress: true,
          precision: 16,
        },
        pagebreak: {
          mode: ['avoid-all', 'css', 'legacy'],
          before: '.talent-section',
          avoid: ['.talent-title', '.talent-role', '.business-roles', 'h3', 'h4'],
        },
        enableLinks: true,
        footer: {
          height: '15mm',
          contents: {
            default:
              '<div style="text-align: center; font-size: 10px; color: #555;">' +
              '<span>Отчет создан с помощью </span>' +
              '<a href="https://my-business.evolution.life/" style="color: #4e73df; text-decoration: none;">Business Evolution AI</a>' +
              '<span> • Страница {{page}} из {{pages}}</span></div>',
          },
        },
        header: {
          height: '15mm',
          contents: {
            default:
              '<div style="text-align: center; font-size: 10px; color: #555; border-bottom: 1px solid #ddd; padding-bottom: 5px;">' +
              'Персональный отчет Business Evolution AI</div>',
          },
        },
      }

      // Создаем PDF
      await html2pdf().set(opt).from(printElement).save()

      // Удаляем временный элемент
      document.body.removeChild(printElement)
    } catch (error) {
      console.error('Ошибка при создании PDF:', error)
      alert('Произошла ошибка при создании PDF. Пожалуйста, попробуйте еще раз.')
    } finally {
      setIsGeneratingPDF(false)
    }
  }

  // Функция для возврата на страницу профиля
  const handleBackToProfile = () => {
    navigate(`/profile/${userId}`)
  }

  // Показываем загрузку
  if (loading) {
    return (
      <div className="container">
        <div className="loading-screen">
          <div className="loader"></div>
          <p>Загрузка полного отчета...</p>
        </div>
      </div>
    )
  }

  // Показываем ошибку
  if (error) {
    return (
      <div className="container">
        <div className="error-screen">
          <h2>Не удалось загрузить отчет</h2>
          <p>{error}</p>
          <button className="btn" onClick={handleBackToProfile}>
            Вернуться к профилю
          </button>
        </div>
      </div>
    )
  }

  // Используем все индексы талантов из профиля
  const validTalentIndices = talentIndices

  console.log('Индексы талантов для отображения:', validTalentIndices)
  console.log('Доступные таланты из базы:', talents)

  return (
    <div className="container">
      <div className="header">
        <h1>Полный отчет профиля</h1>
        <div className="actions">
          <button className="btn-primary" onClick={handleBackToProfile}>
            Вернуться к профилю
          </button>
          <button className="btn pdf-button" onClick={generatePDF} disabled={isGeneratingPDF}>
            {isGeneratingPDF ? 'Создание PDF...' : 'Скачать PDF'}
          </button>
        </div>
      </div>

      <div id="report-content">
        <div className="report-header">
          <h1>Персональный отчёт о бизнес-талантах</h1>
          <div className="personal-info-card">
            <div className="personal-info-item">
              <span className="label">Имя:</span>
              <span className="value">{personalInfo.firstName || 'Не указано'}</span>
            </div>
            <div className="personal-info-item">
              <span className="label">Фамилия:</span>
              <span className="value">{personalInfo.lastName || 'Не указано'}</span>
            </div>
            {personalInfo.position && (
              <div className="personal-info-item">
                <span className="label">Должность:</span>
                <span className="value">{personalInfo.position}</span>
              </div>
            )}
            {personalInfo.company && (
              <div className="personal-info-item">
                <span className="label">Компания:</span>
                <span className="value">{personalInfo.company}</span>
              </div>
            )}
          </div>
        </div>

        {/* Секции талантов - используем точно такой же формат, как в ResultsPage */}
        {validTalentIndices.map((talentId) => {
          const talent = getTalentById(talentId)
          if (!talent) return null

          const talentData = getTalentData(talentId)

          return (
            <TalentSection
              key={talent.Number}
              talent={talent}
              talentData={talentData}
              expandedTalents={expandedTalents}
              toggleTalentIPR={toggleTalentIPR}
              formatSkills={formatSkills}
              getLevels={getLevels}
            />
          )
        })}

        {/* Футер отчета */}
        <div className="report-footer">
          <p> Business Evolution AI</p>
          <div className="report-date">Дата создания: {new Date().toLocaleDateString()}</div>
        </div>
      </div>
    </div>
  )
}

export default FullReportPage
